import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { X, Check, Zap, Sparkles, Shield } from 'lucide-react';
import { saasApi } from '@/services/api';
import type { SaaSPackage } from '@/types';
import { formatMoney } from '@/utils/currency';

interface PackagesModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function PackagesModal({ isOpen, onClose }: PackagesModalProps) {
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'quarterly' | 'annual'>('monthly');

  const { data: packagesData, isLoading } = useQuery({
    queryKey: ['packages'],
    queryFn: async () => {
      const res = await saasApi.getPackages();
      return res.data.message as SaaSPackage[];
    },
    enabled: isOpen,
  });

  if (!isOpen) return null;

  const packages = packagesData || [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900/50 backdrop-blur-sm animate-in fade-in">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col animate-in zoom-in-95 duration-200">
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Available Packages</h2>
            <p className="text-sm text-gray-500">Explore our flexible pricing plans</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            </div>
          ) : (
            <>
              <div className="flex justify-center mb-8">
                <div className="bg-gray-50 rounded-xl border border-gray-200 p-1 inline-flex">
                  {(
                    [
                      ['monthly', 'Monthly'] as const,
                      ...(packages.some((p) => (p.quarterly_price || 0) > 0)
                        ? [['quarterly', 'Quarterly'] as const]
                        : []),
                      ...(packages.some((p) => (p.annual_price || 0) > 0)
                        ? [['annual', 'Annual'] as const]
                        : []),
                    ] as const
                  ).map(([cycle, label]) => (
                    <button
                      key={cycle}
                      onClick={() => setBillingCycle(cycle)}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        billingCycle === cycle
                          ? 'bg-primary-600 text-white shadow-sm'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      {label}
                      {cycle === 'annual' && (
                        <span className="ml-1 text-xs opacity-80">Save 20%</span>
                      )}
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {packages.map((pkg) => (
                  <div
                    key={pkg.package_code}
                    className="card p-6 border border-gray-200 shadow-sm hover:border-primary-200 transition-all"
                  >
                    <div className="text-center mb-6">
                      <div className="w-12 h-12 bg-primary-50 rounded-xl flex items-center justify-center mx-auto mb-4">
                        {pkg.package_code === 'starter' ? (
                          <Zap className="w-6 h-6 text-primary-600" />
                        ) : pkg.package_code === 'business' ? (
                          <Sparkles className="w-6 h-6 text-primary-600" />
                        ) : (
                          <Shield className="w-6 h-6 text-primary-600" />
                        )}
                      </div>
                      <h3 className="font-bold text-gray-900 text-lg">{pkg.package_name}</h3>
                    </div>
                    
                    <div className="text-center mb-6 pb-6 border-b border-gray-100">
                      {(() => {
                        let displayPrice = 0;
                        let displayCycle = 'mo';
                        
                        if (billingCycle === 'annual') {
                          if (pkg.annual_price) { displayPrice = pkg.annual_price; displayCycle = 'year'; }
                          else if (pkg.monthly_price) { displayPrice = pkg.monthly_price * 12; displayCycle = 'year'; }
                        } else if (billingCycle === 'quarterly') {
                          if (pkg.quarterly_price) { displayPrice = pkg.quarterly_price; displayCycle = 'quarter'; }
                          else if (pkg.monthly_price) { displayPrice = pkg.monthly_price * 3; displayCycle = 'quarter'; }
                        } else {
                          // billingCycle === 'monthly'
                          if (pkg.monthly_price) { displayPrice = pkg.monthly_price; displayCycle = 'mo'; }
                          else if (pkg.annual_price) { displayPrice = pkg.annual_price; displayCycle = 'year'; }
                          else if (pkg.quarterly_price) { displayPrice = pkg.quarterly_price; displayCycle = 'quarter'; }
                        }

                        return (
                          <>
                            <span className="text-3xl font-black text-gray-900">
                              {formatMoney(displayPrice)}
                            </span>
                            <span className="text-gray-500 text-sm font-medium ml-1">
                              /{displayCycle}
                            </span>
                          </>
                        );
                      })()}
                    </div>

                    <ul className="space-y-3 mb-6 text-sm text-gray-600">
                      <li className="flex items-center gap-3">
                        <Check className="w-5 h-5 text-emerald-500 shrink-0" />{' '}
                        <span>{pkg.max_users ? `Up to ${pkg.max_users} users` : 'Unlimited users'}</span>
                      </li>
                      <li className="flex items-center gap-3">
                        <Check className="w-5 h-5 text-emerald-500 shrink-0" />{' '}
                        <span>{pkg.max_products
                          ? `${pkg.max_products.toLocaleString()} products`
                          : 'Unlimited products'}</span>
                      </li>
                      <li className="flex items-center gap-3">
                        <Check className="w-5 h-5 text-emerald-500 shrink-0" />{' '}
                        <span>{pkg.max_invoices_per_month
                          ? `${pkg.max_invoices_per_month.toLocaleString()} invoices/mo`
                          : 'Unlimited invoices/mo'}</span>
                      </li>
                      <li className="flex items-center gap-3">
                        <Check className="w-5 h-5 text-emerald-500 shrink-0" />{' '}
                        <span>{pkg.stock_module ? 'Stock management' : 'Basic inventory'}</span>
                      </li>
                      <li className="flex items-center gap-3">
                        <Check className="w-5 h-5 text-emerald-500 shrink-0" />{' '}
                        <span>{pkg.service_module ? 'Service billing' : 'Basic POS'}</span>
                      </li>
                      <li className="flex items-center gap-3">
                        <Check className="w-5 h-5 text-emerald-500 shrink-0" />{' '}
                        <span>{pkg.reports_level} reports</span>
                      </li>
                      {!!pkg.crm_support && (
                        <li className="flex items-center gap-3">
                          <Check className="w-5 h-5 text-emerald-500 shrink-0" /> CRM & support tickets
                        </li>
                      )}
                      {!!pkg.priority_support && (
                        <li className="flex items-center gap-3">
                          <Check className="w-5 h-5 text-emerald-500 shrink-0" /> Priority support
                        </li>
                      )}
                    </ul>

                    {/* Subscription button commented out for future use */}
                    {/* 
                    <button className="w-full py-2.5 rounded-lg font-bold text-sm bg-primary-600 text-white hover:bg-primary-700 transition-colors">
                      Subscribe to {pkg.package_name}
                    </button>
                    */}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        <div className="shrink-0 border-t border-gray-100 bg-gray-50 p-4 flex justify-end">
          <button type="button" onClick={onClose} className="btn-secondary">
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
